/**
 * Migrations.ts
 * Schema version migration and validation for FullGameState in AmericanDreamDB.
 */

import { FullGameState, PlacedFurniture } from '../core/Types';
import { createDefaultGameState } from './StorageService';
import { backfillNonFlagshipSouvenirsFromVisits } from '../content/StateSouvenirsRegistry';

export const CURRENT_SCHEMA_VERSION = 1;

function migrateLegacyStarterFurniturePositions(items: PlacedFurniture[]): PlacedFurniture[] {
  const cloned = items.map((item) => (item && typeof item === 'object' ? { ...item } : item));
  const isCellOccupied = (col: number, row: number, ignoreId: string): boolean => {
    for (const f of cloned) {
      if (!f || f.id === ignoreId) continue;
      const is2x1 = f.typeId === 'bed' || f.typeId === 'desk' || f.typeId === 'sofa';
      const rot = f.rotation || 0;
      const w = is2x1 ? (rot === 90 ? 1 : 2) : 1;
      const h = is2x1 ? (rot === 90 ? 2 : 1) : 1;
      if (col >= f.gridCol && col < f.gridCol + w && row >= f.gridRow && row < f.gridRow + h) {
        return true;
      }
    }
    return false;
  };

  for (const f of cloned) {
    if (!f || typeof f !== 'object') continue;
    // Migrate legacy starter_fridge from (1, 5) flush to Left Wall at (0, 5)
    if (f.id === 'starter_fridge' && f.typeId === 'fridge' && f.gridCol === 1 && f.gridRow === 5) {
      if (!isCellOccupied(0, 5, f.id)) {
        f.gridCol = 0;
        f.gridRow = 5;
      }
    }
    // Migrate legacy starter_desk from (6, 1) flush to Right Wall at (5, 0)
    if (
      f.id === 'starter_desk' &&
      f.typeId === 'desk' &&
      f.gridCol === 6 &&
      f.gridRow === 1 &&
      (f.rotation || 0) === 0
    ) {
      if (!isCellOccupied(5, 0, f.id) && !isCellOccupied(6, 0, f.id)) {
        f.gridCol = 5;
        f.gridRow = 0;
      }
    }
  }
  return cloned;
}

export function migrateGameState(raw: any): FullGameState {
  if (!raw || typeof raw !== 'object') {
    return createDefaultGameState();
  }

  const defaults = createDefaultGameState();

  const migrated: FullGameState = {
    version: CURRENT_SCHEMA_VERSION,
    lastActiveTimestamp: typeof raw.lastActiveTimestamp === 'number' && Number.isFinite(raw.lastActiveTimestamp) && raw.lastActiveTimestamp > 0
      ? raw.lastActiveTimestamp
      : Date.now(),
    stats: {
      money: typeof raw.stats?.money === 'number' && Number.isFinite(raw.stats.money)
        ? Math.max(0, raw.stats.money)
        : defaults.stats.money,
      energy: typeof raw.stats?.energy === 'number' && Number.isFinite(raw.stats.energy)
        ? Math.max(0, Math.min(100, raw.stats.energy))
        : defaults.stats.energy,
      hunger: typeof raw.stats?.hunger === 'number' && Number.isFinite(raw.stats.hunger)
        ? Math.max(0, Math.min(100, raw.stats.hunger))
        : defaults.stats.hunger,
      happiness: typeof raw.stats?.happiness === 'number' && Number.isFinite(raw.stats.happiness)
        ? Math.max(0, Math.min(100, raw.stats.happiness))
        : defaults.stats.happiness,
      lastTickTimestamp: typeof raw.stats?.lastTickTimestamp === 'number' && Number.isFinite(raw.stats.lastTickTimestamp) && raw.stats.lastTickTimestamp > 0
        ? raw.stats.lastTickTimestamp
        : Date.now(),
    },
    activeBuffs: Array.isArray(raw.activeBuffs) ? raw.activeBuffs : defaults.activeBuffs,
    placedFurniture: Array.isArray(raw.placedFurniture)
      ? migrateLegacyStarterFurniturePositions(raw.placedFurniture)
      : defaults.placedFurniture,
    inventory: {
      foodItems: Array.isArray(raw.inventory?.foodItems) ? raw.inventory.foodItems : defaults.inventory.foodItems,
      unlockedSouvenirs: Array.isArray(raw.inventory?.unlockedSouvenirs) ? raw.inventory.unlockedSouvenirs : defaults.inventory.unlockedSouvenirs,
    },
    states: raw.states && typeof raw.states === 'object' ? raw.states : defaults.states,
    visitedAtlasStates: raw.visitedAtlasStates && typeof raw.visitedAtlasStates === 'object'
      ? raw.visitedAtlasStates
      : defaults.visitedAtlasStates,
    ...(raw.visitedCounties && typeof raw.visitedCounties === 'object'
      ? { visitedCounties: raw.visitedCounties }
      : {}),
    visitedCaCounties: raw.visitedCaCounties && typeof raw.visitedCaCounties === 'object'
      ? raw.visitedCaCounties
      : defaults.visitedCaCounties,
    career: raw.career && typeof raw.career === 'object' ? {
      currentJobId: raw.career.currentJobId ?? defaults.career.currentJobId,
      currentTitle: raw.career.currentTitle ?? defaults.career.currentTitle,
      currentTierIndex: raw.career.currentTierIndex ?? defaults.career.currentTierIndex,
      completedCertificates: Array.isArray(raw.career.completedCertificates) ? raw.career.completedCertificates : defaults.career.completedCertificates,
      totalShiftsCompleted: raw.career.totalShiftsCompleted ?? defaults.career.totalShiftsCompleted,
      accountCreatedAt: typeof raw.career.accountCreatedAt === 'number' && Number.isFinite(raw.career.accountCreatedAt)
        ? raw.career.accountCreatedAt
        : defaults.career.accountCreatedAt,
      dailyShiftsDate: typeof raw.career.dailyShiftsDate === 'string'
        ? raw.career.dailyShiftsDate
        : defaults.career.dailyShiftsDate,
      dailyShiftsCompleted: typeof raw.career.dailyShiftsCompleted === 'number' && Number.isFinite(raw.career.dailyShiftsCompleted)
        ? Math.max(0, raw.career.dailyShiftsCompleted)
        : defaults.career.dailyShiftsCompleted,
      certModuleProgress: raw.career.certModuleProgress && typeof raw.career.certModuleProgress === 'object'
        ? raw.career.certModuleProgress
        : defaults.career.certModuleProgress,
    } : defaults.career,
    customPlaceMappings: raw.customPlaceMappings && typeof raw.customPlaceMappings === 'object' ? raw.customPlaceMappings : defaults.customPlaceMappings,
    lastGpsFix: raw.lastGpsFix && typeof raw.lastGpsFix === 'object' && Number.isFinite(raw.lastGpsFix.lat) && Number.isFinite(raw.lastGpsFix.lng)
      ? {
          lat: raw.lastGpsFix.lat,
          lng: raw.lastGpsFix.lng,
          accuracy: typeof raw.lastGpsFix.accuracy === 'number' && Number.isFinite(raw.lastGpsFix.accuracy)
            ? raw.lastGpsFix.accuracy
            : undefined,
          timestamp: typeof raw.lastGpsFix.timestamp === 'number' && Number.isFinite(raw.lastGpsFix.timestamp)
            ? raw.lastGpsFix.timestamp
            : Date.now(),
          stateId: typeof raw.lastGpsFix.stateId === 'string'
            ? raw.lastGpsFix.stateId
            : null,
          countyFips: typeof raw.lastGpsFix.countyFips === 'string'
            ? raw.lastGpsFix.countyFips
            : null,
          countyName: typeof raw.lastGpsFix.countyName === 'string'
            ? raw.lastGpsFix.countyName
            : null,
        }
      : undefined,
    offlineArrivalQueue: Array.isArray(raw.offlineArrivalQueue)
      ? raw.offlineArrivalQueue
          .filter((item: any) => item && typeof item === 'object' && typeof item.location === 'string')
          .map((item: any) => ({
            location: String(item.location),
            timestamp: typeof item.timestamp === 'number' && Number.isFinite(item.timestamp)
              ? item.timestamp
              : Date.now(),
            ...(typeof item.lat === 'number' && Number.isFinite(item.lat) ? { lat: item.lat } : {}),
            ...(typeof item.lng === 'number' && Number.isFinite(item.lng) ? { lng: item.lng } : {}),
            ...(typeof item.accuracy === 'number' && Number.isFinite(item.accuracy) ? { accuracy: item.accuracy } : {}),
            ...(typeof item.countyFips === 'string' ? { countyFips: item.countyFips } : {}),
          }))
      : [],
  };

  backfillNonFlagshipSouvenirsFromVisits(migrated);
  return migrated;
}
